import {
  Radio,
  RadioProps,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
  colorOrToken,
} from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';

import { K8sProviderIcon } from '@cast/features-cluster-ui';

import { PROVIDER_NAME_MAP } from '@cast/utils';

const tooltipByProvider: Partial<Record<K8sProvider, string>> = {
  [K8sProvider.ROSA]: `${PROVIDER_NAME_MAP[K8sProvider.ROSA]} service on AWS`,
  [K8sProvider.KOPS]: `${PROVIDER_NAME_MAP[K8sProvider.KOPS]} service on AWS`,
};

const secondLineByProvider: Partial<Record<K8sProvider, string>> = {
  [K8sProvider.ROSA]: 'on AWS',
  [K8sProvider.KOPS]: 'on AWS',
  [K8sProvider.ANYWHERE]: 'any Cloud or On-Prem',
};

const Description = ({ children }: { children: string }) => (
  <Typography
    component="p"
    variant="P10R"
    color={colorOrToken('grey.400', tokens.theme.fg.structural.secondary)}
    fontSize="8px"
    lineHeight="10px"
  >
    {children}
  </Typography>
);

type Props = Omit<RadioProps, 'onClick'> & {
  provider: `${K8sProvider}`;
  isCompact?: boolean;
  onClick: () => void;
};

export const SelectProviderRadio = ({
  provider,
  onClick,
  isCompact,
  checked,
  ...props
}: Props) => {
  const description = secondLineByProvider[provider];

  return (
    <Tooltip title={tooltipByProvider[provider]} placement="top">
      <Box
        border={colorOrToken(
          checked ? 'blue.500' : 'grey.200',
          checked
            ? tokens.theme.border.selection.primary
            : tokens.theme.border.structural.neutral
        )}
        borderRadius="4px"
        onClick={onClick}
        display="flex"
        gap={isCompact ? '6px' : '4px'}
        alignItems="center"
        justifyContent={isCompact ? 'flex-end' : 'center'}
        flexDirection="column"
        sx={{
          cursor: 'pointer',
          minWidth: 86,
          height: 108,
          ...(isCompact && {
            padding: '0px 8px',
            height: '56px',
            flexDirection: 'row-reverse',
          }),
        }}
        role="radio"
      >
        <Stack
          display="grid"
          gridTemplateColumns={isCompact ? 'min-content 1fr' : '1fr'}
          columnGap={6}
          rowGap={4}
          alignItems="center"
        >
          <Radio
            sx={{
              gridRow: isCompact ? undefined : 3,
              justifySelf: isCompact ? undefined : 'center',
            }}
            size={isCompact ? 'small' : 'medium'}
            checked={checked}
            {...props}
          />
          <Box
            gap="6px"
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
            minHeight={isCompact ? undefined : '58px'}
            flexDirection={isCompact ? 'row' : 'column'}
          >
            <K8sProviderIcon provider={provider} size={isCompact ? 18 : 24} />

            <Stack alignItems={isCompact ? 'flex-start' : 'center'}>
              <Typography
                variant="A2"
                color={colorOrToken(
                  checked ? 'blue.600' : 'grey.600',
                  checked
                    ? tokens.theme.fg.selection.primary
                    : tokens.theme.fg.structural.secondary
                )}
              >
                {PROVIDER_NAME_MAP[provider]}
              </Typography>
              {!isCompact && !!description && (
                <Description>{description}</Description>
              )}
            </Stack>
          </Box>
          {isCompact && !!description && (
            <Box gridColumn={2}>
              <Description>{description}</Description>
            </Box>
          )}
        </Stack>
      </Box>
    </Tooltip>
  );
};
